import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ArticlePage() {
  useEffect(() => {
    document.title = 'Kakobuy Spreadsheet – Kompletny Poradnik dla Początkujących 2026 | KakobuyFinds';
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuyfinds.mom';
  const currentPath = '/artykul/kakobuy-spreadsheet-poradnik/';

  return (
    <>
      <Navbar />
      <main className="pt-[72px]">
        {/* Breadcrumb */}
        <div className="w-full bg-background-50 border-b border-background-200/50">
          <div className="w-full px-6 md:px-10 py-3">
            <div className="max-w-[1280px] mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500">
                <Link to="/" className="hover:text-primary-500 transition-colors">Strona główna</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-700 font-medium">Poradnik Kakobuy Spreadsheet</span>
              </nav>
            </div>
          </div>
        </div>

        {/* Article Header */}
        <section className="w-full bg-accent-50/50">
          <div className="w-full px-6 md:px-10 py-16 md:py-24">
            <div className="max-w-[800px] mx-auto">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent-100/60 rounded-full mb-4">
                <span className="text-xs font-medium text-accent-700">Poradnik</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                Kakobuy Spreadsheet – Kompletny Poradnik dla Początkujących 2026
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                Wszystko, co musisz wiedzieć o Kakobuy Spreadsheet – od podstaw po zaawansowane techniki wyszukiwania produktów. Praktyczny przewodnik dla polskich użytkowników.
              </p>
              <div className="flex items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  Aktualizacja: Lipiec 2026
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  15 minut czytania
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  KakobuyFinds
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="w-full bg-background-50">
          <div className="w-full px-6 md:px-10 py-12 md:py-16">
            <div className="max-w-[800px] mx-auto">
              <article className="prose-custom space-y-8 text-foreground-700 leading-relaxed">
                {/* Table of Contents */}
                <div className="bg-secondary-50 rounded-2xl p-6 mb-10">
                  <h2 className="text-lg font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                    <i className="ri-list-check-2 text-secondary-600"></i>
                    Spis treści
                  </h2>
                  <ol className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-700 text-xs font-medium">1</span>
                      Czym jest Kakobuy Spreadsheet i dlaczego go potrzebujesz?
                    </li>
                    <li className="flex items-center gap-2 text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-700 text-xs font-medium">2</span>
                      Jak rozpocząć pracę z Kakobuy Spreadsheet?
                    </li>
                    <li className="flex items-center gap-2 text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-700 text-xs font-medium">3</span>
                      Przegląd kategorii produktowych
                    </li>
                    <li className="flex items-center gap-2 text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-700 text-xs font-medium">4</span>
                      Jak znaleźć najlepsze Kakobuy Finds?
                    </li>
                    <li className="flex items-center gap-2 text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-700 text-xs font-medium">5</span>
                      Bezpieczeństwo zakupów – kompletny przewodnik
                    </li>
                    <li className="flex items-center gap-2 text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-700 text-xs font-medium">6</span>
                      Wysyłka do Polski – koszty, czas i formalności celne
                    </li>
                    <li className="flex items-center gap-2 text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-700 text-xs font-medium">7</span>
                      Najczęściej zadawane pytania (FAQ)
                    </li>
                  </ol>
                </div>

                {/* Section 1 */}
                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                    1. Czym jest Kakobuy Spreadsheet i dlaczego go potrzebujesz?
                  </h2>
                  <p className="text-sm md:text-base mb-4">
                    <strong>Kakobuy Spreadsheet</strong> to rewolucyjne narzędzie, które całkowicie zmienia sposób, w jaki robisz zakupy na platformie Kakobuy. Wyobraź sobie, że zamiast spędzać długie godziny na ręcznym przeszukiwaniu dziesiątek stron z ofertami, masz wszystkie najlepsze produkty zebrane w jednym, przejrzyście zorganizowanym arkuszu. To właśnie oferuje <strong>Kakobuy Spreadsheet</strong>.
                  </p>
                  <p className="text-sm md:text-base mb-4">
                    W przeciwieństwie do standardowego wyszukiwania na platformie, gdzie musisz samodzielnie filtrować setki wyników i sprawdzać każdy produkt z osobna, <strong>Kakobuy Spreadsheet</strong> dostarcza Ci gotowe, zweryfikowane listy produktów. Każdy wpis zawiera nie tylko nazwę i kategorię produktu, ale także bezpośredni link, co pozwala zaoszczędzić mnóstwo czasu podczas codziennych poszukiwań.
                  </p>
                  <p className="text-sm md:text-base mb-4">
                    Dla polskich użytkowników <strong>Kakobuy Spreadsheet</strong> jest szczególnie wartościowy, ponieważ zawiera dodatkowe informacje na temat dostępności dostawy do Polski, szacunkowych kosztów wysyłki oraz opinii innych kupujących z naszego kraju. To sprawia, że narzędzie to jest nie tylko wygodne, ale także praktyczne i dostosowane do lokalnych potrzeb.
                  </p>
                  <div className="bg-accent-50/60 rounded-xl p-5 my-6 border-l-2 border-accent-400">
                    <p className="text-sm font-medium text-foreground-800">
                      <i className="ri-lightbulb-line text-accent-500 mr-1.5"></i>
                      <strong>Wskazówka:</strong> Nowi użytkownicy <strong>Kakobuy Spreadsheet</strong> oszczędzają średnio 4-6 godzin tygodniowo na wyszukiwaniu produktów w porównaniu do standardowego przeglądania platformy.
                    </p>
                  </div>
                </section>

                {/* Section 2 */}
                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                    2. Jak rozpocząć pracę z Kakobuy Spreadsheet?
                  </h2>
                  <p className="text-sm md:text-base mb-4">
                    Rozpoczęcie przygody z <strong>Kakobuy Spreadsheet</strong> jest prostsze niż mogłoby się wydawać. Oto szczegółowa instrukcja, która krok po kroku przeprowadzi Cię przez cały proces.
                  </p>
                  <ol className="space-y-4 my-4">
                    <li className="flex items-start gap-3 text-sm md:text-base">
                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-semibold text-sm shrink-0 mt-0.5">1</span>
                      <div>
                        <strong className="text-foreground-900">Otwórz Kakobuy Spreadsheet</strong>
                        <p className="text-foreground-600 mt-1">Przejdź do arkusza korzystając z przycisku &quot;Kakobuy Spreadsheet&quot; na naszej stronie. Arkusz jest hostowany na platformie zewnętrznej i dostępny od ręki, bez konieczności rejestracji.</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3 text-sm md:text-base">
                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-semibold text-sm shrink-0 mt-0.5">2</span>
                      <div>
                        <strong className="text-foreground-900">Wybierz kategorię</strong>
                        <p className="text-foreground-600 mt-1">Arkusz jest podzielony na 14 kategorii produktowych. Wybierz tę, która Cię interesuje – od butów i odzieży, przez akcesoria, aż po perfumy.</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3 text-sm md:text-base">
                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-semibold text-sm shrink-0 mt-0.5">3</span>
                      <div>
                        <strong className="text-foreground-900">Przeglądaj produkty</strong>
                        <p className="text-foreground-600 mt-1">Każdy produkt w <strong>Kakobuy Spreadsheet</strong> posiada bezpośredni link, nazwę i dodatkowe informacje. Kliknij w link, aby przejść do strony produktu.</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3 text-sm md:text-base">
                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-semibold text-sm shrink-0 mt-0.5">4</span>
                      <div>
                        <strong className="text-foreground-900">Porównuj i wybieraj</strong>
                        <p className="text-foreground-600 mt-1">Dzięki uporządkowanej strukturze <strong>Kakobuy Spreadsheet</strong> możesz łatwo porównać różne produkty, ich ceny i opcje, a następnie dokonać najlepszego wyboru.</p>
                      </div>
                    </li>
                  </ol>
                </section>

                {/* Section 3 */}
                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                    3. Przegląd kategorii produktowych w Kakobuy Spreadsheet
                  </h2>
                  <p className="text-sm md:text-base mb-4">
                    <strong>Kakobuy Spreadsheet</strong> oferuje produkty w 14 starannie wyselekcjonowanych kategoriach. Oto szczegółowy przegląd każdej z nich:
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-6">
                    {[
                      { cat: 'Buty (Shoes)', desc: 'Największa kategoria – setki modeli sneakersów i obuwia sportowego' },
                      { cat: 'Bluzy/Swetry (Hoodies/Sweaters)', desc: 'Najnowsze trendy streetwearowe i klasyczne modele' },
                      { cat: 'T-Shirty (T-Shirts)', desc: 'Koszulki od podstawowych po limitowane edycje' },
                      { cat: 'Kurtki (Jackets)', desc: 'Kurtki na każdą porę roku – od lekkich po zimowe' },
                      { cat: 'Spodnie/Szorty (Pants/Shorts)', desc: 'Jeansy, spodnie dresowe, szorty i wiele więcej' },
                      { cat: 'Nakrycia głowy (Headwear)', desc: 'Czapki, kapelusze i inne akcesoria na głowę' },
                      { cat: 'Zestawy (Sets)', desc: 'Gotowe zestawy ubrań – idealne na prezent lub dla siebie' },
                      { cat: 'Bielizna (Underwear)', desc: 'Bielizna codzienna i sportowa w dobrej cenie' },
                      { cat: 'Koszulki sportowe (Jersey)', desc: 'Koszulki piłkarskie i sportowe – oryginalne i repliki' },
                      { cat: 'Akcesoria (Accessories)', desc: 'Zegarki, okulary, biżuteria i inne dodatki' },
                      { cat: 'Torby (Bags)', desc: 'Plecaki, torby na ramię, nerki i saszetki' },
                      { cat: 'Paski (Belts)', desc: 'Skórzane, materiałowe i designerskie paski' },
                      { cat: 'Perfumy (Perfumes)', desc: 'Popularne zapachy – oryginały i odpowiedniki' },
                      { cat: 'Inne (Other Stuff)', desc: 'Wszystko, co nie pasuje do pozostałych kategorii' },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2.5 p-3 bg-secondary-50 rounded-xl">
                        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 text-xs shrink-0 mt-0.5">{idx + 1}</span>
                        <div>
                          <p className="text-sm font-semibold text-foreground-800">{item.cat}</p>
                          <p className="text-xs text-foreground-500 mt-0.5">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 4 */}
                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                    4. Jak znaleźć najlepsze Kakobuy Finds?
                  </h2>
                  <p className="text-sm md:text-base mb-4">
                    Znajdowanie najlepszych <strong>Kakobuy Finds</strong> wymaga nieco wprawy, ale z pomocą <strong>Kakobuy Spreadsheet</strong> staje się to znacznie prostsze. Oto kilka sprawdzonych strategii, które pomogą Ci wyłowić prawdziwe perełki:
                  </p>
                  <ul className="space-y-3 my-4">
                    <li className="flex items-start gap-2 text-sm md:text-base">
                      <i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i>
                      <span><strong className="text-foreground-800">Regularnie sprawdzaj aktualizacje</strong> – <strong>Kakobuy Spreadsheet</strong> jest aktualizowany co tydzień, więc zawsze znajdziesz świeże produkty.</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm md:text-base">
                      <i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i>
                      <span><strong className="text-foreground-800">Korzystaj z wielu kategorii</strong> – nie ograniczaj się do jednej kategorii; często najlepsze znaleziska czekają w mniej oczywistych miejscach.</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm md:text-base">
                      <i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i>
                      <span><strong className="text-foreground-800">Porównuj ceny</strong> – <strong>Kakobuy Spreadsheet</strong> ułatwia porównywanie, dzięki czemu zawsze znajdziesz najlepszą ofertę.</span>
                    </li>
                  </ul>
                </section>

                {/* Section 5 */}
                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                    5. Bezpieczeństwo zakupów – kompletny przewodnik
                  </h2>
                  <p className="text-sm md:text-base mb-4">
                    Jednym z najczęściej zadawanych pytań przez nowych użytkowników <strong>Kakobuy Spreadsheet</strong> jest kwestia bezpieczeństwa. Czy zakupy przez linki z arkusza są bezpieczne? Odpowiedź brzmi: tak, pod warunkiem przestrzegania kilku podstawowych zasad.
                  </p>
                  <p className="text-sm md:text-base mb-4">
                    Wszystkie linki w <strong>Kakobuy Spreadsheet</strong> prowadzą bezpośrednio do platformy Kakobuy, która posiada własne mechanizmy ochrony kupujących. Dodatkowo, arkusz zawiera tylko produkty od zweryfikowanych sprzedawców z pozytywnymi opiniami. Mimo to, zawsze warto sprawdzić ocenę sprzedawcy przed dokonaniem zakupu.
                  </p>
                  <div className="bg-primary-50 rounded-xl p-5 my-6">
                    <h4 className="font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                      <i className="ri-shield-check-line text-primary-500"></i>
                      Zasady bezpiecznych zakupów z Kakobuy Spreadsheet:
                    </h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-primary-400 mt-0.5">•</span>
                        <span>Zawsze sprawdzaj ocenę i liczbę transakcji sprzedawcy</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary-400 mt-0.5">•</span>
                        <span>Czytaj opinie innych kupujących – szczególnie tych z Polski</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary-400 mt-0.5">•</span>
                        <span>Korzystaj z bezpiecznych metod płatności oferowanych przez platformę</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary-400 mt-0.5">•</span>
                        <span>W razie wątpliwości skontaktuj się ze sprzedawcą przed zakupem</span>
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Section 6 */}
                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                    6. Wysyłka do Polski – koszty, czas i formalności celne
                  </h2>
                  <p className="text-sm md:text-base mb-4">
                    Dla polskich użytkowników <strong>Kakobuy Spreadsheet</strong> kluczową kwestią jest wysyłka. Platforma Kakobuy oferuje kilka metod dostawy do Polski, a <strong>Kakobuy Spreadsheet</strong> pomaga wybrać tę najbardziej optymalną.
                  </p>
                  <p className="text-sm md:text-base mb-4">
                    Średni czas dostawy do Polski wynosi od 10 do 25 dni roboczych, w zależności od wybranej metody wysyłki. Koszty przesyłki zaczynają się już od około 30-50 zł za małe paczki, a dla większych zamówień mogą wynosić 80-150 zł. Warto pamiętać, że <strong>Kakobuy Spreadsheet</strong> zawiera orientacyjne informacje o kosztach wysyłki dla poszczególnych kategorii produktów.
                  </p>
                  <div className="bg-amber-50 rounded-xl p-5 my-6 border-l-2 border-amber-400">
                    <h4 className="font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                      <i className="ri-alert-line text-amber-600"></i>
                      Uwaga dotycząca cła:
                    </h4>
                    <p className="text-sm text-foreground-700">
                      Przy zamówieniach o wartości powyżej 150 EUR mogą zostać naliczone dodatkowe opłaty celne i podatek VAT. <strong>Kakobuy Spreadsheet</strong> zawiera orientacyjne wartości produktów, co pomaga w planowaniu budżetu zakupowego z uwzględnieniem potencjalnych opłat celnych.
                    </p>
                  </div>
                </section>

                {/* Section 7 - FAQ */}
                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                    7. Najczęściej zadawane pytania (FAQ)
                  </h2>
                  <div className="space-y-4">
                    {[
                      {
                        q: 'Czy Kakobuy Spreadsheet jest darmowy?',
                        a: 'Tak, Kakobuy Spreadsheet jest całkowicie darmowy. Nie ma żadnych ukrytych opłat ani subskrypcji.',
                      },
                      {
                        q: 'Jak często aktualizowany jest Kakobuy Spreadsheet?',
                        a: 'Arkusz jest aktualizowany regularnie, zazwyczaj co tydzień. Nowe produkty są dodawane, a nieaktualne linki usuwane.',
                      },
                      {
                        q: 'Czy potrzebuję konta, aby korzystać z Kakobuy Spreadsheet?',
                        a: 'Nie, do przeglądania arkusza nie jest wymagane konto. Potrzebujesz jedynie konta na platformie Kakobuy do dokonywania zakupów.',
                      },
                      {
                        q: 'Czy mogę sugerować produkty do dodania do arkusza?',
                        a: 'Tak, społeczność Kakobuy Spreadsheet aktywnie współtworzy arkusz. Sugestie są zawsze mile widziane.',
                      },
                      {
                        q: 'Czy Kakobuy Spreadsheet działa na urządzeniach mobilnych?',
                        a: 'Tak, arkusz jest w pełni kompatybilny z urządzeniami mobilnymi, dzięki czemu możesz przeglądać produkty na smartfonie.',
                      },
                    ].map((faq, idx) => (
                      <details key={idx} className="group bg-secondary-50 rounded-xl overflow-hidden">
                        <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-secondary-100 transition-colors">
                          <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.q}</span>
                          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-600 group-open:rotate-45 transition-transform shrink-0">
                            <i className="ri-add-line text-sm"></i>
                          </span>
                        </summary>
                        <div className="px-4 pb-4">
                          <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                        </div>
                      </details>
                    ))}
                  </div>
                </section>

                {/* CTA */}
                <div className="bg-primary-50/70 rounded-2xl p-8 text-center my-10">
                  <h3 className="text-xl font-bold text-foreground-900 mb-2">
                    Gotowy, aby zacząć korzystać z Kakobuy Spreadsheet?
                  </h3>
                  <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                    Dołącz do tysięcy zadowolonych użytkowników i odkryj najlepsze Kakobuy Finds już dziś.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="group inline-flex items-center gap-2 px-6 py-3.5 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <span>Przejdź do Kakobuy Spreadsheet</span>
                      <span className="w-7 h-7 flex items-center justify-center bg-background-50/20 rounded-full group-hover:translate-x-0.5 transition-transform">
                        <i className="ri-arrow-right-line text-sm"></i>
                      </span>
                    </a>
                    <Link
                      to="/"
                      className="px-6 py-3.5 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
                    >
                      ← Powrót na stronę główną
                    </Link>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Structured Data: Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Kakobuy Spreadsheet – Kompletny Poradnik dla Początkujących 2026',
            description: 'Wszystko, co musisz wiedzieć o Kakobuy Spreadsheet – od podstaw po zaawansowane techniki wyszukiwania produktów. Praktyczny przewodnik dla polskich użytkowników.',
            author: { '@type': 'Organization', name: 'KakobuyFinds' },
            datePublished: '2026-07-16',
            dateModified: '2026-07-16',
            inLanguage: 'pl',
            publisher: { '@type': 'Organization', name: 'KakobuyFinds' },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `${siteUrl}${currentPath}`,
            },
          }),
        }}
      />

      {/* Structured Data: FAQPage */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'Czy Kakobuy Spreadsheet jest darmowy?',
                acceptedAnswer: { '@type': 'Answer', text: 'Tak, Kakobuy Spreadsheet jest całkowicie darmowy. Nie ma żadnych ukrytych opłat ani subskrypcji.' },
              },
              {
                '@type': 'Question',
                name: 'Jak często aktualizowany jest Kakobuy Spreadsheet?',
                acceptedAnswer: { '@type': 'Answer', text: 'Arkusz jest aktualizowany regularnie, zazwyczaj co tydzień. Nowe produkty są dodawane, a nieaktualne linki usuwane.' },
              },
              {
                '@type': 'Question',
                name: 'Czy potrzebuję konta, aby korzystać z Kakobuy Spreadsheet?',
                acceptedAnswer: { '@type': 'Answer', text: 'Nie, do przeglądania arkusza nie jest wymagane konto. Potrzebujesz jedynie konta na platformie Kakobuy do dokonywania zakupów.' },
              },
              {
                '@type': 'Question',
                name: 'Czy Kakobuy Spreadsheet działa na urządzeniach mobilnych?',
                acceptedAnswer: { '@type': 'Answer', text: 'Tak, arkusz jest w pełni kompatybilny z urządzeniami mobilnymi, dzięki czemu możesz przeglądać produkty na smartfonie.' },
              },
            ],
          }),
        }}
      />

      {/* Structured Data: BreadcrumbList */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Strona główna', item: `${siteUrl}/` },
              { '@type': 'ListItem', position: 2, name: 'Poradnik Kakobuy Spreadsheet', item: `${siteUrl}${currentPath}` },
            ],
          }),
        }}
      />
    </>
  );
}